import { useEffect, useMemo, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import api from '../api';
import { groupSurveyResults } from '../utils/surveyResults';
import SurveyQuestion from '../components/cards/SurveyQuestion';
import Card from '../components/cards/Card';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import Breadcrumb from '../components/Breadcrumb';
import EmptyContent from '../components/EmptyContent';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import Button from '../components/Button';
import { ReactComponent as ExportIcon } from '../assets/images/icons/ic-export.svg';
import styles from './Survey.module.css';

export default function Survey() {
    const { survey_id } = useParams();
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exportError, setExportError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [reload, setReload] = useState(0);
    const groups = useMemo(() => survey ? groupSurveyResults(survey) : {}, [survey]);
    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError(''); setSurvey(null);
        api.get(`/admin/surveys/${survey_id}`).then(data => { if (!cancelled) setSurvey(data); })
            .catch(() => { if (!cancelled) setError('Impossibile caricare i risultati del questionario.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [survey_id, reload]);
    const downloadReport = async () => {
        setExporting(true); setExportError('');
        try {
            const response = await api.get(`/admin/surveys/${survey_id}/report`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url; link.download = `${survey.title}_report.csv`;
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { setExportError('Impossibile scaricare il report. Riprova.'); }
        finally { setExporting(false); }
    };
    return <HeaderFooterLayout><HelmetProvider><Helmet><title>{survey?.title || 'Risultati questionario'}</title></Helmet></HelmetProvider>
        <div className={styles.container}><main className={styles.page}>
            <Breadcrumb items={[{ label: 'Questionari', to: '/surveys' }, { label: survey?.title || 'Risultati' }]} />
            <header className={styles.header}><div><h1>{survey?.title || 'Risultati questionario'}</h1><p>Consulta le risposte e i partecipanti per ogni domanda.</p></div><Button appearance="subtle" additionalClass={styles.primaryAction} disabled={!survey || loading || exporting} onClick={downloadReport}><ExportIcon aria-hidden="true" style={{ width: 11, height: 11 }} />{exporting ? 'Esportazione…' : 'Esporta'}</Button></header>
            {loading && <p role="status" className={styles.message}>Caricamento risultati…</p>}
            {error && <div role="alert" className={styles.error}>{error} <Button appearance="text" onClick={() => setReload(value => value + 1)}>Riprova</Button></div>}
            {exportError && <p role="alert" className={styles.error}>{exportError}</p>}
            {!loading && survey && <>
                {survey.description && <div className={styles.description}><Markdown rehypePlugins={[rehypeRaw]}>{survey.description}</Markdown></div>}
                {Object.entries(groups).map(([group, questions]) => <Card key={group} appearance="modern" title={group === 'undefined' || !group ? 'Domande' : group}>
                    <div className={styles.questions}>{questions.map(question => <SurveyQuestion key={question.id} question={question} surveyId={survey_id} />)}</div>
                </Card>)}
                {!Object.keys(groups).length && <EmptyContent title="Nessuna domanda" message="Questo questionario non contiene ancora domande." />}
            </>}
        </main></div>
    </HeaderFooterLayout>;
}
